import React, { useEffect, useState } from "react";
import { Button, DatePicker, LabelValue, Select } from "../../../../Components";
import { FaPlaneArrival, FaPlaneDeparture } from "react-icons/fa";
import { TbArrowsExchange } from "react-icons/tb";
import AirportSearch from "../../Inputs/AirportSearch";
import { useNavigate } from "react-router-dom";
import { useUserConfig } from "../../../../Hooks/useUserConfig";
import SelectPassengers from "../../Inputs/SelectPassengers";
import moment from "moment";
import { CLASSES } from "Utils/constants";
import { saveRecentFlightSearch } from "../../../../Utils/recentSearches";
import useUserProfile from "../../../../Hooks/useUserProfile";

function FlightSearch({ from, to, date, range, passengers, cabinClass, mode, optimizee, onSearch = () => {} }) {
	const NAVIGATE = useNavigate();
	const { role, checkUserPermission } = useUserConfig();
	const { profile } = useUserProfile();
	const userId = profile?.id;
	const isUser = role === "USER";
	const [highlightSwappedFields, setHighlightSwappedFields] = useState(false);
	const [errors, setErrors] = useState({});

	const MODES = [
		{ label: "One Way", value: "oneway" },
		{ label: "Round Trip", value: "round" },
	];

	const [optimize, setOptimize] = useState(() => {
		if (optimizee === false){
			return false;
		}
		else{
			const saved = localStorage.getItem("optimize");
  			return saved === null ? true : JSON.parse(saved);
		}
	});

	useEffect(() => {
		localStorage.setItem("optimize", JSON.stringify(optimize));
	}, [optimize]);

	const [Values, setValues] = useState({
		from: from || {},
		to: to || {},
		date: date || moment().valueOf(),
		range: range,
		passengers: passengers || {
			adult: 1,
			child: 0,
			infant: 0,
		},
		cabinClass: cabinClass || CLASSES[0],
		mode: mode || MODES[0],
	});

	useEffect(() => {
		if (!from && !to && !date && !range && !passengers && !cabinClass && !mode) {
			return;
		}
		setValues((prev) => ({
			...prev,
			...(from ? { from } : {}),
			...(to ? { to } : {}),
			...(date ? { date } : {}),
			...(range ? { range } : {}),
			...(passengers ? { passengers } : {}),
			...(cabinClass ? { cabinClass } : {}),
			...(mode ? { mode } : {}),
		}));
	}, [from, to, date, range, passengers, cabinClass, mode]);

	const updateValues = (obj) => {
		setValues((prev) => ({ ...prev, ...obj }));
	};

	const switchMode = (option) => {
		if (option.value === "oneway") {
			updateValues({ mode: MODES[0] });
		} else {
			updateValues({
				mode: MODES[1],
				range: {
					startDate: Values.range?.startDate || Values.date,
					endDate: Values.range?.endDate,
				},
			});
		}
	};

	const clearError = (field) => {
		setErrors((prev) => ({ ...prev, [field]: undefined }));
	};

	const setDepartureDate = (date) => {
		const currentReturn = Values.range?.endDate;
		updateValues({
			date,
			range: {
				startDate: date,
				endDate: currentReturn && currentReturn < date ? undefined : currentReturn,
			},
		});
		clearError("departure");
	};

	const setReturnDate = (endDate) => {
		updateValues({
			range: {
				startDate: Values.range?.startDate || Values.date,
				endDate,
			},
		});
		clearError("return");
	};

	const swapAirports = () => {
		const temp = Values.from;
		updateValues({ from: Values.to, to: temp });
		setHighlightSwappedFields(true);
	};

	useEffect(() => {
		if (!highlightSwappedFields) {
			return undefined;
		}

		const timeout = setTimeout(() => {
			setHighlightSwappedFields(false);
		}, 2000);

		return () => clearTimeout(timeout);
	}, [highlightSwappedFields]);

	const airportInputStyle = highlightSwappedFields
		? {
				backgroundColor: "rgba(245, 158, 11, 0.08)",
				boxShadow: "inset 0 0 0 1px rgba(245, 158, 11, 0.22)",
			}
		: undefined;

	const fromAirportField = (
		<AirportSearch
			selected={Values.from}
			label="Traveling from"
			onSelect={(from) => {
				updateValues({ from });
				clearError("from");
			}}
			onFocus={() => clearError("from")}
			error={errors.from}
			placeholder="Travelling from"
			icon={<FaPlaneDeparture />}
			inputClassName="transition-[background-color,box-shadow] duration-300"
			inputStyle={airportInputStyle}
		/>
	);

	const toAirportField = (
		<AirportSearch
			selected={Values.to}
			label="Going to"
			onSelect={(to) => {
				updateValues({ to });
				clearError("to");
			}}
			onFocus={() => clearError("to")}
			error={errors.to}
			placeholder="Going to"
			icon={<FaPlaneArrival />}
			inputClassName="transition-[background-color,box-shadow] duration-300"
			inputStyle={airportInputStyle}
		/>
	);

	const searchFlights = () => {
		let nextError;
		if (!Values.from?.airportCode) {
			nextError = { from: "Please select From airport" };
		} else if (!Values.to?.airportCode) {
			nextError = { to: "Please select To airport" };
		} else if (Values.mode?.value === "oneway" && !Values.date) {
			nextError = { date: "Please select date" };
		} else if (Values.mode?.value === "round" && !Values.range?.startDate) {
			nextError = { departure: "Please select departure date" };
		} else if (Values.mode?.value === "round" && !Values.range?.endDate) {
			nextError = { return: "Please select return date" };
		}
		if (nextError) {
			setErrors(nextError);
			return;
		}
		setErrors({});
		onSearch();
		if (userId) {
			saveRecentFlightSearch({ values: { ...Values, userId }, optimize });
		}
		if (Values.mode.value === MODES[0].value) {
			NAVIGATE(
				`/app/travel/flights/oneway?fromCity=${Values.from?.cityName}&from=${Values.from?.airportName}&fromCode=${Values.from?.airportCode}&toCity=${Values.to?.cityName}&to=${Values.to?.airportName}&toCode=${Values.to?.airportCode}&date=${Values.date}&adult=${Values.passengers?.adult}&child=${Values.passengers?.child}&infant=${Values.passengers?.infant}&cabinClass=${Values.cabinClass?.value}&optimize=${optimize}`
			);
		} else {
			NAVIGATE(
				`/app/travel/flights/round?fromCity=${Values.from?.cityName}&from=${Values.from?.airportName}&fromCode=${Values.from?.airportCode}&toCity=${Values.to?.cityName}&to=${Values.to?.airportName}&toCode=${Values.to?.airportCode}&date=${Values.range.startDate}&returnDate=${Values.range.endDate}&adult=${Values.passengers?.adult}&child=${Values.passengers?.child}&infant=${Values.passengers?.infant}&cabinClass=${Values.cabinClass?.value}&optimize=${optimize}`
			);
		}
	};

	return (
		<div className="space-y-4">
			<div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4">
				<LabelValue className="text-dark font-medium">Search Options</LabelValue>
				<div className="flex flex-row items-center gap-2 sm:gap-3 w-full sm:w-auto">
					<div className="flex-1 min-w-0 sm:flex-none sm:w-auto">
						<Select
							variant="outlined"
							btnClass="btn-sm w-full sm:w-auto !min-w-0 sm:!min-w-[7rem]"
							onChange={switchMode}
							selected={Values.mode}
							options={MODES}
						/>
					</div>
					<div className="flex-1 min-w-0 sm:flex-none sm:w-auto">
						<Select
							variant="outlined"
							btnClass="btn-sm w-full sm:w-auto !min-w-0 sm:!min-w-[7rem]"
							onChange={(cabinClass) => updateValues({ cabinClass })}
							selected={Values.cabinClass}
							options={CLASSES}
						/>
					</div>
				</div>
			</div>
			<div className="p-3 md:p-4 rounded-lg bg-light border border-surface/30 shadow-sm">
				<div className="flex flex-col gap-4 lg:flex-row lg:items-stretch">
					<div className="flex-1 flex flex-col gap-4 min-w-0">
						<div className="md:hidden">
							<div className="min-w-0">{fromAirportField}</div>
							{isUser && (
								<div className="relative z-10 -mb-4 translate-y-1 flex justify-end">
									<button
										type="button"
										onClick={swapAirports}
										className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-light ring-1 ring-surface active:scale-95 transition-transform"
										aria-label="Swap origin and destination"
									>
										<TbArrowsExchange className="h-5 w-5 rotate-90 text-secondary" />
									</button>
								</div>
							)}
							<div className="min-w-0">{toAirportField}</div>
						</div>
						<div className="hidden md:flex md:items-end md:gap-4">
							<div className="flex-1 min-w-0">{fromAirportField}</div>
							{isUser && (
								<div className="shrink-0 w-8 flex justify-center pb-2">
									<button
										type="button"
										onClick={swapAirports}
										className="inline-flex items-center justify-center w-8 h-8 rounded-md hover:bg-canvas transition-colors"
										aria-label="Swap origin and destination"
									>
										<TbArrowsExchange className="w-5 h-5 text-secondary transition-transform duration-200 hover:rotate-180" />
									</button>
								</div>
							)}
							<div className="flex-1 min-w-0">{toAirportField}</div>
						</div>
						<div className="flex flex-col gap-4 md:flex-row md:items-end md:gap-4">
							<div className="flex-1 min-w-0">
								{Values?.mode?.value === "oneway" ? (
									<DatePicker backDateSelection={false} label="Travel Date" error={errors.date} onFocus={() => clearError("date")} setDate={(date) => { updateValues({ date }); clearError("date"); }} date={Values.date} />
								) : (
									<div className="flex gap-4">
										<div className="w-1/2 min-w-0">
											<DatePicker backDateSelection={false} label="Departure Date" error={errors.departure} onFocus={() => clearError("departure")} setDate={setDepartureDate} date={Values.range?.startDate || Values.date} />
										</div>
										<div className="w-1/2 min-w-0">
											<DatePicker backDateSelection={false} label="Return Date" error={errors.return} onFocus={() => clearError("return")} minDate={Values.range?.startDate || Values.date} setDate={setReturnDate} date={Values.range?.endDate} />
										</div>
									</div>
								)}
							</div>
							{isUser && checkUserPermission("TA") && <div className="hidden md:block shrink-0 w-8" />}
							{checkUserPermission("TA") && (
								<div className="flex-1 min-w-0">
									<SelectPassengers
										passengers={Values.passengers}
										onChange={(obj) =>
											updateValues({
												passengers: {
													...Values.passengers,
													...obj,
												},
											})
										}
										label="Passengers"
									/>
								</div>
							)}
						</div>
					</div>

					<div className="flex items-end">
						<Button onClick={searchFlights} variant="primary" className="h-10 w-full !py-0 text-2xl font-semibold tracking-wide lg:w-[10rem]">
							Search
						</Button>
					</div>
				</div>
			</div>
		</div>
	);
}

export default FlightSearch;
